/**
 * What a Plan's preview looks like, as the model gives it and as the view draws it. Kept free of the app, so the
 * Assistant's loop can read previews without loading it.
 */
export type PreviewChange = 'add' | 'remove' | 'change';

/**
 * The preview's colors, shared by the view and the Plan's legend. Chosen away from the app's own: its chords are green,
 * its webs yellow, its walls pink, and its dimensions teal.
 */
export const PREVIEW_COLORS: Record<PreviewChange, string> = { add: '#3d9bff', remove: '#ff4d4f', change: '#b57bff' };

interface Point {
  readonly x: number;
  readonly y: number;
}

export interface PreviewShape {
  readonly change: PreviewChange;
  readonly points: readonly Point[];
  readonly closed: boolean;
}

/**
 * What a Plan says it will change, as the model gave it: shapes by their points, and existing things by name.
 */
/**
 * A load in a preview, in the truss's coordinates: a point load acts at `at`, a distributed one from `from` to `to`, both
 * along `direction` (straight down if not given).
 */
export interface PreviewLoad {
  readonly change: PreviewChange;
  readonly at?: Point;
  readonly from?: Point;
  readonly to?: Point;
  readonly direction: Point;
}

export interface PreviewRequest {
  /** The truss the preview is on, in its coordinates; `null` for the layout, in plan coordinates. */
  readonly truss: string | null;
  readonly shapes: readonly PreviewShape[];
  readonly items: readonly { readonly name: string; readonly change: PreviewChange }[];
  readonly loads: readonly PreviewLoad[];
}

/**
 * A Plan's preview, ready to draw: every shape in the coordinates of the truss or layout it's on.
 */
export interface PlanPreview {
  readonly trussGuid: string | null;
  readonly shapes: readonly PreviewShape[];
  /** Names the preview gave that the app couldn't find, so the designer knows the drawing is incomplete. */
  readonly notFound: readonly string[];
}

const CHANGES: readonly PreviewChange[] = ['add', 'remove', 'change'];

const POINT_SCHEMA = { type: 'object', properties: { x: { type: 'number' }, y: { type: 'number' } } };

export const PREVIEW_SCHEMA = {
  type: 'object',
  description:
    'What the plan changes, drawn on the view while the designer decides: green for new, red for removed, amber for ' +
    'changed. Give it whenever the plan adds, removes, moves, or reshapes something.',
  properties: {
    truss: {
      type: 'string',
      description: 'The truss the changes are on; points are then in its coordinates. Leave it out for the layout.',
    },
    shapes: {
      type: 'array',
      description: 'New or changed geometry: a member or wall as its centerline (two points), an outline as closed.',
      items: {
        type: 'object',
        properties: {
          change: { type: 'string', enum: CHANGES },
          points: {
            type: 'array',
            items: POINT_SCHEMA,
          },
          closed: { type: 'boolean' },
        },
        required: ['change', 'points'],
      },
    },
    loads: {
      type: 'array',
      description:
        'Loads the plan adds or changes on the truss, where they act: a point load `at` a point, a distributed load ' +
        '`from` and `to` points on its member, with the direction it pushes (straight down if left out).',
      items: {
        type: 'object',
        properties: {
          change: { type: 'string', enum: CHANGES },
          at: POINT_SCHEMA,
          from: POINT_SCHEMA,
          to: POINT_SCHEMA,
          direction: { ...POINT_SCHEMA, description: 'Which way the load pushes, such as {x: 0, y: -1} for down.' },
        },
        required: ['change'],
      },
    },
    items: {
      type: 'array',
      description:
        'Existing things the plan removes or changes, by name: members and loads (by their get_loads id) on the ' +
        'truss; walls, truss envelopes, work lines, floor areas, and roof planes (by id) in the layout.',
      items: {
        type: 'object',
        properties: { name: { type: 'string' }, change: { type: 'string', enum: CHANGES } },
        required: ['name', 'change'],
      },
    },
  },
};

const DOWN: Point = { x: 0, y: -1 };

function isChange(value: unknown): value is PreviewChange {
  return CHANGES.includes(value as PreviewChange);
}

function readPoint(value: unknown): Point | null {
  const point = value as { x?: unknown; y?: unknown } | null;
  return typeof point?.x === 'number' && typeof point.y === 'number' && Number.isFinite(point.x + point.y)
    ? { x: point.x, y: point.y }
    : null;
}

/**
 * Reads the preview the model gave with a Plan, dropping anything malformed rather than refusing the Plan over it.
 */
export function parsePreviewRequest(value: unknown): PreviewRequest | null {
  if (typeof value !== 'object' || value === null) return null;
  const { truss, shapes, items, loads } = value as {
    truss?: unknown;
    shapes?: unknown;
    items?: unknown;
    loads?: unknown;
  };

  const parsedShapes = (Array.isArray(shapes) ? shapes : []).flatMap((shape): PreviewShape[] => {
    const { change, points, closed } = (shape ?? {}) as { change?: unknown; points?: unknown; closed?: unknown };
    const parsedPoints = (Array.isArray(points) ? points : []).map(readPoint);
    if (!isChange(change) || parsedPoints.length < 2 || parsedPoints.some((point) => point == null)) return [];
    return [{ change, points: parsedPoints as Point[], closed: closed === true }];
  });
  const parsedItems = (Array.isArray(items) ? items : []).flatMap((item) => {
    const { name, change } = (item ?? {}) as { name?: unknown; change?: unknown };
    return typeof name === 'string' && name.trim() !== '' && isChange(change) ? [{ name: name.trim(), change }] : [];
  });

  const parsedLoads = (Array.isArray(loads) ? loads : []).flatMap((load): PreviewLoad[] => {
    const { change, at, from, to, direction } = (load ?? {}) as Record<string, unknown>;
    if (!isChange(change)) return [];
    const parsedDirection = readPoint(direction);
    const pushes =
      parsedDirection != null && Math.hypot(parsedDirection.x, parsedDirection.y) > 0 ? parsedDirection : DOWN;
    const point = readPoint(at);
    if (point != null) return [{ change, at: point, direction: pushes }];
    const start = readPoint(from);
    const end = readPoint(to);
    return start != null && end != null ? [{ change, from: start, to: end, direction: pushes }] : [];
  });

  if (parsedShapes.length === 0 && parsedItems.length === 0 && parsedLoads.length === 0) return null;
  return {
    truss: typeof truss === 'string' && truss.trim() !== '' ? truss.trim() : null,
    shapes: parsedShapes,
    items: parsedItems,
    loads: parsedLoads,
  };
}
